import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import {
  showCustomNotification,
  showNotification,
} from '@vk-hr-tek/core/notifications';

import {
  CompanyEventsStat,
  CompanyGroupUsers,
  GetCompanyEventTypesResponse,
  GetEventTypesCustomTypesResponse,
  GetEventTypesRoleGroupsQuery,
  GetEventTypesRoleGroupsResponse,
  GetEventTypeResponse,
  SettingsService,
} from '@app/gen/settings';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';

import { EventTypeFormMapper } from '../../../mappers';
import { SettingsState, SettingsWithRootState } from '../../settings.state';
import { CreateEventTypeDto } from '../../../dto';
import { getCompanies } from '../companies';

import { awaitDispatch } from './eventType.actions';

const DELAY_DEFAULT_MS = 1000;

const delay = (time: number) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(null), time);
  });

export const getEventTypeCompanyOptions = createAsyncThunk<
  GetEventTypesCustomTypesResponse &
    GetCompanyEventTypesResponse &
    GetEventTypesRoleGroupsResponse,
  GetEventTypesRoleGroupsQuery,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventTypeCompanyOptions',
  async (query, { rejectWithValue, getState, dispatch, extra: { inject } }) => {
    const eventTypeService = inject(SettingsService);

    try {
      const companies = getState().settings?.companies;

      if (!companies?.length) {
        await awaitDispatch(dispatch(getCompanies()));
      }

      if (!query.company_id) {
        return { custom_types: [], event_types: [], groups: [] };
      }

      const [eventTypes, customTypes, roleGroups] = await Promise.all([
        eventTypeService.getCompanyEventTypes({
          params: { company_id: query.company_id },
        }),
        eventTypeService.getEventTypesCustomTypes({ query }),
        eventTypeService.getEventTypesRoleGroups({ query }),
      ]);

      return { ...customTypes, ...eventTypes, ...roleGroups };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEditEventType = createAsyncThunk<
  GetEventTypeResponse,
  { eventTypeId: string },
  ThunkExtra
>(
  'settings/getEditEventType',
  async ({ eventTypeId }, { extra: { inject } }) => {
    const settingsService = inject(SettingsService);
    return settingsService.getEventType({
      params: { event_type_id: eventTypeId },
    });
  },
);

export const createEventType = createAsyncThunk<
  { id: string },
  CreateEventTypeDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/createEventType',
  async (dto, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const settingsService = inject(SettingsService);
      const eventTypeCreateMapper = inject(EventTypeFormMapper);

      const body = eventTypeCreateMapper.processFormToRequestBody(dto);

      const { id } = await settingsService.createEventType({ body });

      await delay(DELAY_DEFAULT_MS);

      dispatch(showNotification('Тип заявки создан'));

      return { id };
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось создать тип заявки',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEventType = createAsyncThunk<
  void,
  { eventTypeId?: string } & CreateEventTypeDto,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateEventType',
  async (
    { eventTypeId, ...dto },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    if (!eventTypeId) return;

    try {
      const settingsService = inject(SettingsService);
      const eventTypeCreateMapper = inject(EventTypeFormMapper);

      const body = eventTypeCreateMapper.processFormToRequestBody(dto);

      await settingsService.updateEventType({
        params: { event_type_id: eventTypeId },
        body,
      });

      dispatch(showNotification('Тип заявки успешно изменен'));
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось изменить тип заявки',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsEventTypeEditReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getEventTypeCompanyOptions.pending, (state) => {
    state.eventTypeCompanyOptions.status = 'loading';
    state.eventTypeCompanyOptions.error = null;
    state.eventTypeCompanyOptions.customTypeOptions = [];
    state.eventTypeCompanyOptions.eventTypeCompanyEventTypes = [];
    state.eventTypeCompanyOptions.eventTypeGroupOptions = [];
  });
  builder.addCase(
    getEventTypeCompanyOptions.fulfilled,
    (state, { payload }) => {
      state.eventTypeCompanyOptions.status = 'complete';
      state.eventTypeCompanyOptions.error = null;
      state.eventTypeCompanyOptions.customTypeOptions =
        payload.custom_types || [];
      state.eventTypeCompanyOptions.eventTypeCompanyEventTypes =
        payload.event_types || [];
      state.eventTypeCompanyOptions.eventTypeGroupOptions =
        payload.groups || [];
    },
  );
  builder.addCase(
    getEventTypeCompanyOptions.rejected,
    (state, { payload, error }) => {
      state.eventTypeCompanyOptions.status = 'failed';
      state.eventTypeCompanyOptions.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.eventTypeCompanyOptions.customTypeOptions = [];
      state.eventTypeCompanyOptions.eventTypeCompanyEventTypes = [];
      state.eventTypeCompanyOptions.eventTypeGroupOptions = [];
    },
  );

  builder.addCase(getEditEventType.pending, (state) => {
    state.eventTypeEdit.status = 'loading';
    state.eventTypeEdit.error = null;
    state.eventTypeEdit.item = null;
  });
  builder.addCase(getEditEventType.fulfilled, (state, { payload }) => {
    state.eventTypeEdit.status = 'complete';
    state.eventTypeEdit.error = null;
    state.eventTypeEdit.item = payload;
  });
  builder.addCase(getEditEventType.rejected, (state, { payload, error }) => {
    state.eventTypeEdit.status = 'failed';
    state.eventTypeEdit.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
    state.eventTypeEdit.item = null;
  });
};
